import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { Button, message, Popconfirm, Space, Spin, Switch, Table } from "antd";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { serverFetch } from "@server";
import { renderCheckbox } from "@extensions/helper";
import { deleteButton } from "@extensions/design";
import { useViewportBreakpoint } from "../../hooks/useViewportBreakpoint";
import TaskUser from "./task-user";
import "./task-users.css";

const TaskUsers = () => {
  const user = useSelector((s) => s.user);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const isMobile = useViewportBreakpoint() === "mobile";

  const getData = () => {
    if (!user?.companyId) return;
    setLoading(true);
    serverFetch(`plan/dep?companyId=${user.companyId}`)
      .then(data => {
        setLoading(false);
        setItems(data);
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  useEffect(() => {
    if (user?.companyId) getData();
  }, [user?.companyId]);

  const onDelete = record => {
    setLoading(true);
    serverFetch(`plan/dep/${user?.companyId}/${record.id}`, { method: "DELETE" })
      .then(() => getData())
      .catch(e => message.error(e.userMessage))
      .finally(() => setLoading(false));
  };

  const saveActive = (record, active) => {
    setLoading(true);
    serverFetch("plan/dep", {
      method: "PUT",
      bodyData: { ...record, active, companyId: user?.companyId },
    })
      .then(() => getData())
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  const onCreate = () => setSelected({ companyId: user?.companyId, id: 0, userName: "", active: true, name: "" });

  const onEdit = record => setSelected({ ...record });

  const columns = [
    { dataIndex: "id", title: "#", width: 60 },
    { dataIndex: "active", title: "Активен", width: 80, render: renderCheckbox },
    { dataIndex: "userName", title: "Телефон/почта" },
    { dataIndex: "name", title: "Наименование" },
    {
      title: "",
      render: (_, record) => (
        <div className="actions-on-hover">
          <Button type="link" size="small" onClick={() => onEdit(record)}>Изменить</Button>
          {deleteButton(loading, record.name, () => onDelete(record))}
        </div>
      ),
    },
  ];

  const onItemClose = refresh => {
    setSelected(null);
    if (refresh) getData();
  };

  return (
    <div className={isMobile ? "task-users-root task-users-root--mobile" : "task-users-root"}>
      {isMobile ? (
        <>
          <div className="task-users-mobile-toolbar">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={onCreate}
              disabled={loading}
              aria-label="Добавить"
              className="task-users-mobile-toolbar-btn"
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={getData}
              loading={loading}
              disabled={loading}
              aria-label="Обновить"
              className="task-users-mobile-toolbar-btn"
            />
          </div>
          <div className="task-users-mobile-scroll">
            <Spin spinning={loading}>
              <div className="task-users-mobile-list">
                {items.map(record => (
                  <div key={record.id} className="task-users-mobile-card">
                    <div className="task-users-mobile-card-actions">
                      <Button
                        type="default"
                        size="small"
                        icon={<EditOutlined />}
                        className="task-users-mobile-card-icon-btn"
                        onClick={() => onEdit(record)}
                        aria-label="Изменить"
                      />
                      <Popconfirm
                        title="Удалить?"
                        description="Действие нельзя отменить"
                        okText="Удалить"
                        cancelText="Отмена"
                        onConfirm={() => onDelete(record)}
                        okButtonProps={{ danger: true }}
                      >
                        <Button
                          danger
                          type="default"
                          size="small"
                          icon={<DeleteOutlined />}
                          className="task-users-mobile-card-icon-btn"
                          disabled={loading}
                          aria-label="Удалить"
                        />
                      </Popconfirm>
                    </div>
                    <div className="task-users-mobile-card-row1">
                      <div className="task-users-mobile-card-active">
                        <Switch
                          checked={!!record.active}
                          disabled={loading}
                          size="small"
                          onChange={checked => saveActive(record, checked)}
                          aria-label="Активен"
                        />
                      </div>
                      <span className="task-users-mobile-card-name">{record.name || "—"}</span>
                    </div>
                    <div className="task-users-mobile-card-phone">{record.userName || "—"}</div>
                  </div>
                ))}
              </div>
            </Spin>
          </div>
        </>
      ) : (
        <>
          <Space style={{ marginBottom: 16 }}>
            <Button onClick={onCreate} type="primary">
              Добавить
            </Button>
            <Button onClick={getData} loading={loading}>
              Обновить
            </Button>
          </Space>
          <Table
            dataSource={items}
            loading={loading}
            columns={columns}
            pagination={false}
            size="small"
            rowKey="id"
          />
        </>
      )}
      {selected && <TaskUser onClose={onItemClose} itemDefault={selected} companyId={user?.companyId} />}
    </div>
  );
};

export default TaskUsers;
